import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';

import { apiUrl, directApiOrigin, shouldRetryAgainstApiServer } from '../http/api-url';

export const AUTH_ROLES = [
  'Admin',
  'Clerk',
  'Procurement Officer',
  'Reviewer',
  'Approver',
] as const;

export type AuthRole = (typeof AUTH_ROLES)[number];

export type LoginCredentials = {
  email: string;
  password: string;
};

export type LoginOptions = {
  rememberSession: boolean;
};

export type AuthPermission = {
  id: number;
  module: string;
  action: string;
};

type LoginUser = {
  id: number;
  name: string;
  email: string;
  role_id: number;
  role: AuthRole;
  is_active: boolean;
  permissions: AuthPermission[];
};

type LoginResponse = {
  access_token: string;
  token_type: string;
  expires_at: string;
  user: LoginUser;
};

type CurrentUserResponse = {
  user: LoginUser;
};

export type AuthSession = {
  accessToken: string;
  tokenType: string;
  userId: number;
  username: string;
  email: string;
  roleId: number;
  role: AuthRole;
  permissions: AuthPermission[];
  rememberSession: boolean;
  issuedAt: string;
  expiresAt: string;
};

const AUTH_SESSION_KEY = 'procurement_portal_auth_session';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly sessionSignal = signal<AuthSession | null>(this.readSession());

  readonly session = this.sessionSignal.asReadonly();

  login(credentials: LoginCredentials, options: LoginOptions): Observable<AuthSession> {
    const loginUrl = apiUrl('/auth/login');
    return this.postLogin(loginUrl, credentials).pipe(
      catchError((error: unknown) => {
        if (this.shouldRetryAgainstApiServer(error)) {
          return this.postLogin(`${this.directApiOrigin}${loginUrl}`, credentials);
        }

        return throwError(() => error);
      }),
      map((response) => this.toSession(response, options)),
      tap((session) => this.persistSession(session)),
    );
  }

  logout(): Observable<void> {
    return this.http.post(apiUrl('/auth/logout'), {}).pipe(
      catchError(() => of(null)),
      tap(() => this.clearSession()),
      map(() => undefined),
    );
  }

  refreshSession(): Observable<AuthSession> {
    const currentSession = this.getActiveSession();

    if (!currentSession) {
      return throwError(() => new Error('No active session.'));
    }

    const meUrl = apiUrl('/auth/me');
    return this.getCurrentUser(meUrl).pipe(
      catchError((error: unknown) => {
        if (this.shouldRetryAgainstApiServer(error)) {
          return this.getCurrentUser(`${this.directApiOrigin}${meUrl}`);
        }

        return throwError(() => error);
      }),
      map((response) => this.toRefreshedSession(currentSession, response.user)),
      tap((session) => this.persistSession(session)),
    );
  }

  getActiveSession(): AuthSession | null {
    const session = this.sessionSignal();

    if (!session) {
      return null;
    }

    if (this.isSessionExpired(session)) {
      this.clearSession();
      return null;
    }

    return session;
  }

  hasSession(): boolean {
    return this.getActiveSession() !== null;
  }

  invalidateSession(): void {
    this.clearSession();
  }

  private toSession(response: LoginResponse, options: LoginOptions): AuthSession {
    return {
      accessToken: response.access_token,
      tokenType: response.token_type,
      userId: response.user.id,
      username: response.user.name,
      email: response.user.email,
      roleId: response.user.role_id,
      role: response.user.role,
      permissions: response.user.permissions,
      rememberSession: options.rememberSession,
      issuedAt: new Date().toISOString(),
      expiresAt: response.expires_at,
    };
  }

  private toRefreshedSession(currentSession: AuthSession, user: LoginUser): AuthSession {
    return {
      ...currentSession,
      userId: user.id,
      username: user.name,
      email: user.email,
      roleId: user.role_id,
      role: user.role,
      permissions: user.permissions,
    };
  }

  private postLogin(url: string, credentials: LoginCredentials): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(url, credentials);
  }

  private getCurrentUser(url: string): Observable<CurrentUserResponse> {
    return this.http.get<CurrentUserResponse>(url);
  }

  private shouldRetryAgainstApiServer(error: unknown): boolean {
    return shouldRetryAgainstApiServer(error);
  }

  private get directApiOrigin(): string {
    return directApiOrigin();
  }

  private persistSession(session: AuthSession): void {
    const primaryStorage = this.getStorage(session.rememberSession ? 'local' : 'session');
    const secondaryStorage = this.getStorage(session.rememberSession ? 'session' : 'local');

    primaryStorage?.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    secondaryStorage?.removeItem(AUTH_SESSION_KEY);
    this.sessionSignal.set(session);
  }

  private clearSession(): void {
    this.getStorage('local')?.removeItem(AUTH_SESSION_KEY);
    this.getStorage('session')?.removeItem(AUTH_SESSION_KEY);
    this.sessionSignal.set(null);
  }

  private readSession(): AuthSession | null {
    return this.readStoredSession('session') ?? this.readStoredSession('local');
  }

  private readStoredSession(storageType: 'local' | 'session'): AuthSession | null {
    const storage = this.getStorage(storageType);
    const rawSession = storage?.getItem(AUTH_SESSION_KEY);

    if (!rawSession) {
      return null;
    }

    try {
      const parsedSession: unknown = JSON.parse(rawSession);

      if (this.isAuthSession(parsedSession)) {
        if (this.isSessionExpired(parsedSession)) {
          storage?.removeItem(AUTH_SESSION_KEY);
          return null;
        }

        return parsedSession;
      }
    } catch {
      storage?.removeItem(AUTH_SESSION_KEY);
    }

    return null;
  }

  private isAuthSession(value: unknown): value is AuthSession {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const session = value as Partial<AuthSession>;

    return (
      typeof session.accessToken === 'string' &&
      typeof session.tokenType === 'string' &&
      typeof session.userId === 'number' &&
      typeof session.username === 'string' &&
      typeof session.email === 'string' &&
      typeof session.roleId === 'number' &&
      typeof session.rememberSession === 'boolean' &&
      typeof session.issuedAt === 'string' &&
      typeof session.expiresAt === 'string' &&
      Array.isArray(session.permissions) &&
      session.permissions.every((permission) => this.isAuthPermission(permission)) &&
      this.isAuthRole(session.role)
    );
  }

  private isAuthRole(role: unknown): role is AuthRole {
    return typeof role === 'string' && (AUTH_ROLES as readonly string[]).includes(role);
  }

  private isAuthPermission(permission: unknown): permission is AuthPermission {
    if (!permission || typeof permission !== 'object') {
      return false;
    }

    const value = permission as Partial<AuthPermission>;
    return (
      typeof value.id === 'number' &&
      typeof value.module === 'string' &&
      typeof value.action === 'string'
    );
  }

  private isSessionExpired(session: AuthSession): boolean {
    const expiresAt = new Date(session.expiresAt).getTime();

    if (Number.isNaN(expiresAt)) {
      return true;
    }

    return expiresAt <= Date.now();
  }

  private getStorage(storageType: 'local' | 'session'): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return storageType === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  }
}
